import type {LinkProvidedProps} from '@visx/network/lib/types';
import type {FC} from 'react';
import React from 'react';

import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import type {InboxGraphLink} from './inboxGraphTypes';
import {getNodeHeight} from './inboxGraphUtils';

/**
 * Returns a link between two nodes in an inbox graph.
 */
export const LinkComponent: FC<LinkProvidedProps<InboxGraphLink>> = ({link}) => {
  const {source, target} = link;
  const ARC_RADIUS = 24;
  const H_LINE_DISTANCE = 32;
  const LINK_DISTANCE_FROM_SOURCE = source.type === 'bundles' ? 0 : 8;
  const LINK_DISTANCE_FROM_TARGET = target.type === 'bundles' ? 0 : 8;
  const {x: startX} = source;
  const {x: endX} = target;
  const startY = source.y + getNodeHeight(source) / 2 + LINK_DISTANCE_FROM_SOURCE;
  const endY = target.y - getNodeHeight(target) / 2 - LINK_DISTANCE_FROM_TARGET;
  const turnY = link.target.type === 'main_inbox' ? endY - H_LINE_DISTANCE : startY + H_LINE_DISTANCE;
  const ARC_DIRECTION = endX > startX ? 1 : -1;
  const theme = useUserTheme();

  const getD = () => {
    const maybeArrow = ['inbox', 'main_inbox'].includes(target.type)
      ? ` ${/* Assuming cursor is already at (endX, endY) position. */ ''}
        L ${endX - 5} ${endY - 5} ${/* Draw a line following vector (-5, -5). */ ''}
        M ${endX} ${endY + 1} ${
          /* Place cursor back ton (endX, endY) position (+1 to take stroke width into consideration) */ ''
        }
        L ${endX + 5} ${endY - 5} ${/* Draw a line following vector (5, -5). */ ''}
      `
      : '';

    // If no horizontal difference between source and target, just draw a vertical line.
    if (startX === endX) {
      return `M ${startX} ${startY} V ${endY} ${maybeArrow}`;
    }

    // Else we follow the vertical line, turn, horizontal line, turn vertical line patter.
    return `
      M ${startX} ${startY} ${/* put the cursor to (startX, startY). */ ''}
      V ${turnY - ARC_RADIUS} ${/* draw a vertical line down to (startX, turnY - ARC_RADIUS). */ ''}
      Q ${startX} ${turnY} ${startX + ARC_DIRECTION * ARC_RADIUS} ${turnY} ${
      /* draw a quarter of circle of radius ARC_RADIUS. */ ''
    }
      H ${endX - ARC_DIRECTION * ARC_RADIUS} ${
      /* draw a horizontal line up to (endX - ARC_DIRECTION * ARC_RADIUS, turnY). */ ''
    }
      Q ${endX} ${turnY} ${endX} ${turnY + ARC_RADIUS} ${
      /* draw a quarter of circle of radius ARC_RADIUS. */ ''
    }
      V ${endY} ${/* draw a vertical line down to (endX, endY). */ ''}
      ${maybeArrow}
    `;
  };

  return <path d={getD()} strokeWidth={2} stroke={theme.greys.shade50} fill="transparent" />;
};
